import { Injectable, signal, computed, OnDestroy } from '@angular/core';
import { fromEvent, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

/**
 * Tailwind CSS breakpoints (in pixels)
 * @see https://tailwindcss.com/docs/responsive-design
 */
export const BREAKPOINTS = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
} as const;

export type Breakpoint = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';

/**
 * Reactive service for responsive design.
 * Provides signals that update when the window is resized.
 *
 * Breakpoints match Tailwind CSS:
 *   - xs: < 640px (mobile)
 *   - sm: 640px - 767px
 *   - md: 768px - 1023px (tablet)
 *   - lg: 1024px - 1279px (small desktop)
 *   - xl: 1280px - 1535px (desktop)
 *   - 2xl: >= 1536px (large desktop)
 *
 * Usage:
 *   responsiveService = inject(ResponsiveService);
 *
 * In template:
 *   @if (responsiveService.isMobile()) { mobile layout }
 *   @if (responsiveService.breakpoint() === 'lg') { specific breakpoint }
 *
 * For computed values:
 *   panelSizes = computed(() => this.responsiveService.ltLg() ? [50, 50] : [35, 65]);
 */
@Injectable({ providedIn: 'root' })
export class ResponsiveService implements OnDestroy {
  private readonly _screenWidth = signal(window.innerWidth);
  private readonly _resizeSubscription: Subscription;

  /** Current screen width in pixels */
  readonly screenWidth = this._screenWidth.asReadonly();

  /** Current breakpoint name */
  readonly breakpoint = computed<Breakpoint>(() => {
    const width = this._screenWidth();
    if (width < BREAKPOINTS.sm) return 'xs';
    if (width < BREAKPOINTS.md) return 'sm';
    if (width < BREAKPOINTS.lg) return 'md';
    if (width < BREAKPOINTS.xl) return 'lg';
    if (width < BREAKPOINTS['2xl']) return 'xl';
    return '2xl';
  });

  // ===== "Less than" breakpoint checks (lt = less than) =====

  /** < 640px (smaller than sm) */
  readonly ltSm = computed(() => this._screenWidth() < BREAKPOINTS.sm);

  /** < 768px (smaller than md) */
  readonly ltMd = computed(() => this._screenWidth() < BREAKPOINTS.md);

  /** < 1024px (smaller than lg) */
  readonly ltLg = computed(() => this._screenWidth() < BREAKPOINTS.lg);

  /** < 1280px (smaller than xl) */
  readonly ltXl = computed(() => this._screenWidth() < BREAKPOINTS.xl);

  /** < 1536px (smaller than 2xl) */
  readonly lt2xl = computed(() => this._screenWidth() < BREAKPOINTS['2xl']);

  // ===== "Greater than or equal" breakpoint checks (gte) =====

  /** >= 640px */
  readonly gteSm = computed(() => this._screenWidth() >= BREAKPOINTS.sm);

  /** >= 768px */
  readonly gteMd = computed(() => this._screenWidth() >= BREAKPOINTS.md);

  /** >= 1024px */
  readonly gteLg = computed(() => this._screenWidth() >= BREAKPOINTS.lg);

  /** >= 1280px */
  readonly gteXl = computed(() => this._screenWidth() >= BREAKPOINTS.xl);

  /** >= 1536px */
  readonly gte2xl = computed(() => this._screenWidth() >= BREAKPOINTS['2xl']);

  // ===== Semantic aliases =====

  /** < 640px - phone-sized screens */
  readonly isMobile = this.ltSm;

  /** >= 768px && < 1024px - tablet-sized screens */
  readonly isTablet = computed(() => this._screenWidth() >= BREAKPOINTS.md && this._screenWidth() < BREAKPOINTS.lg);

  /** >= 1024px - desktop-sized screens */
  readonly isDesktop = this.gteLg;

  constructor() {
    this._resizeSubscription = fromEvent(window, 'resize')
      .pipe(debounceTime(100))
      .subscribe(() => {
        this._screenWidth.set(window.innerWidth);
      });
  }

  ngOnDestroy(): void {
    this._resizeSubscription.unsubscribe();
  }
}
